import { i18n } from '@shared/i18n';
import IdField from '@shared/fields/id-field';
import StringField from '@shared/fields/string-field';
import BooleanField from '@shared/fields/boolean-field';
import ImagesField from '@shared/fields/images-field';
import DateTimeField from '@shared/fields/date-time-field';
import DateTimeRangeField from '@shared/fields/date-time-range-field';
import DateModelField from '@shared/fields/date-model-field';
import GenericField from '@shared/fields/generic-field';
import EnumeratorField from '@shared/fields/enumerator-field';
import PhoneField from '@shared/fields/phone-field';
import { GenericModel } from '@shared/model/generic-model';
import { PropertyField } from '@modules/property/property-field';
import { SubpropertyField } from '@modules/subproperty/subproperty-field';
import { ProgramField } from '@modules/program/program-field';
import { SubprogramField } from '@modules/subprogram/subprogram-field';
import Roles from '@security/roles';
import RolesFactory from '@shared/factories/roles-factory'
import ManageFactory from '@shared/factories/manage-factory'
import EmailField from '@shared/fields/email-field';
import EmailsField from '@shared/fields/emails-field';
import RolesField from '@shared/fields/roles-field';
import ManageTypeField from '@shared/fields/manage-type-field';
import AddressField from '@shared/fields/address-field';
import getSharedEnumeratorField from '@shared/fields/helpers/getSharedEnumeratorField';

function label(name) {
    return i18n(`user.fields.${name}`);
}

const fields = ({ dateFormat, timeFormat }) => ({
    id: new IdField('id', label('id')),
    authenticationUid: new StringField('authenticationUid', label('authenticationUid')),
    firstName: new StringField('firstName', label('firstName'), { max: 80, required: true }),
    lastName: new StringField('lastName', label('lastName'), { max: 175, required: true }),
    gender: new EnumeratorField('gender', label('gender'), getSharedEnumeratorField('genderBinary'), { required: true }),
    birthdate: new DateModelField('birthdate', label('birthdate'), { required: true, onlyPastDate: true, minAge: 18, dateFormat }),
    address: new AddressField('address', label('address')),
    identDoc: new StringField('identDoc', label('identDoc'), { "required": true }),
    manageType: new ManageTypeField('manageType', label('manageType'), (manageType) => new ManageFactory(manageType).optionsValues, {}),
    manage: new StringField('manage', label('manage'), { required: true, }),
    password: new StringField('password', label('password'), { required: true, min: 6, }),
    confirmPassword: new StringField('confirmPassword', label('confirmPassword.label'), { required: true, equalTo: 'password', }),
    fullName: new StringField('fullName', label('fullName')),
    email: new EmailField('email', label('email'), { required: true }),
    role: new EnumeratorField('role', label('role'), Roles.selectOptions),
    rememberMe: new BooleanField('rememberMe', label('rememberMe')),
    disabledAsStatus: new BooleanField('disabled', label('status'), { noLabel: i18n('user.enabled'), yesLabel: i18n('user.disabled'), }),
    disabled: new BooleanField('disabled', label('disabled'), { noLabel: i18n('user.enabled'), yesLabel: i18n('user.disabled'), }),
    phoneNumber: new PhoneField('phoneNumber', label('phoneNumber'), { required: true }),
    avatarsIam: new ImagesField('avatars', label('avatars'), 'users/avatars/iam', { max: 1, required: false }),
    avatarsProfile: new ImagesField('avatars', label('avatars'), (id) => `users/avatars/profile/${id}`, { max: 1, required: false }),
    emails: new EmailsField('emails', label('emails'), { required: true, }),
    rolesRequired: new RolesField('roles', label('roles'), (manageType) => new RolesFactory().createRoles({ manageType }), { required: true, }),
    property: PropertyField.relationToOne('property', label('property'), { "required": true }),
    subproperty: SubpropertyField.relationToOne('subproperty', label('subproperty'), { "required": true }),
    program: ProgramField.relationToOne('program', label('program'), { "required": true }),
    subprogram: SubprogramField.relationToOne('subprogram', label('subprogram'), { "required": true }),
    roles: new RolesField('roles', label('roles'), (manageType) => new RolesFactory().createRoles({ manageType })),
    lastLogin: new DateTimeField('lastLogin', label('lastLogin'), { dateFormat, timeFormat }),
    createdAt: new DateTimeField('createdAt', label('createdAt'), { dateFormat, timeFormat }),
    updatedAt: new DateTimeField('updatedAt', label('updatedAt'), { dateFormat, timeFormat }),
    createdAtRange: new DateTimeRangeField('createdAtRange', label('createdAtRange')),
    roleUser: new GenericField('roleUser', label('roleUser')),
    status: new EnumeratorField('status', label('status'), [{ id: 'enabled', label: i18n('user.enabled'), }, { id: 'disabled', label: i18n('user.disabled'), },]),
    tosAccepted: new BooleanField('tosAccepted', label('tosAccepted.label'), {
        mandatoryTrue: true,
        linkCaptionPrivacy: i18n('user.fields.tosAccepted.linkCaptionPrivacy'),
        linkHrefPrivacy: i18n('user.fields.tosAccepted.linkHrefPrivacy'),
        linkCaptionTerms: i18n('user.fields.tosAccepted.linkCaptionTerms'),
        linkHrefTerms: i18n('user.fields.tosAccepted.linkHrefTerms'),
        mandatoryErrorCaption: i18n('user.fields.tosAccepted.validation'),
    }),
    inscriptionTutorConfirmed: new BooleanField('inscriptionTutorConfirmed', label('inscriptionTutorConfirmed.label'), {
        mandatoryTrue: true,
        linkCaption: i18n('user.fields.inscriptionTutorConfirmed.linkCaption'),
        linkHref: i18n('user.fields.inscriptionTutorConfirmed.linkHref'),
        mandatoryErrorCaption: i18n('user.fields.inscriptionTutorConfirmed.validation'),
    }),
});

export class UserModel extends GenericModel {
    constructor({ dateFormat, timeFormat } = {}) {
        super();

        this.dateFormat = dateFormat;
        this.timeFormat = timeFormat;
    }

    fields() {
        const { dateFormat, timeFormat } = this;
        return fields({ dateFormat, timeFormat });
    }


    presenter(row, fieldName) {
        const fields = this.fields();

        if (!fields[fieldName]) {
            throw new Error(`${fieldName} not found`);
        }

        return fields[fieldName].forPresenter(
            row[fields[fieldName].name],
        );
    }
}
